import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

export const ALL_ROUTES = [
  { path: "/dashboard", permissions: ["CAN_VIEW_DASHBOARD"] },
  { path: "/users", permissions: ["CAN_MANAGE_USERS"] },
  { path: "/permissions", permissions: ["CAN_MANAGE_ROLES", "CAN_MANAGE_PERMISSIONS"] },
  { path: "/security", permissions: ["CAN_MANAGE_SECURITY"] },
  { path: "/hotels", permissions: ["CAN_MANAGE_HOTELS"] },
  { path: "/hotel-settings", permissions: ["CAN_MANAGE_SETTINGS"] },
  { path: "/room-management", permissions: ["CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"] },
  { path: "/pricing-management", permissions: ["CAN_VIEW_RATES", "CAN_MANAGE_RATES", "CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"] },
  { path: "/reservations", permissions: ["CAN_VIEW_BOOKING_CALENDAR", "CAN_VIEW_RESERVATIONS", "CAN_CREATE_BOOKING"] },
  { path: "/housekeeping", permissions: ["CAN_MONITOR_CLEANLINESS", "CAN_VIEW_HOUSEKEEPING", "CAN_VIEW_HS_TASKS", "CAN_COMPLETE_HS_TASK", "CAN_UPDATE_ROOM_STATUS"] },
  { path: "/restaurant", permissions: ["CAN_MODIFY_MENU", "CAN_APPROVE_ORDER", "CAN_VIEW_RESTAURANT", "CAN_VIEW_KOT", "CAN_UPDATE_ORDER_STATUS"] },
  { path: "/inventory", permissions: ["CAN_VIEW_STOCK_DASHBOARD", "CAN_APPROVE_REQUISITION", "CAN_RAISE_STOCK_REQUEST", "CAN_VIEW_INVENTORY"] },
  { path: "/staff", permissions: ["CAN_MANAGE_STAFF", "IS_STAFF"] },
  { path: "/finance", permissions: ["CAN_VIEW_FINANCIALS", "CAN_APPROVE_EXPENSE", "CAN_VIEW_NIGHT_AUDIT"] },
  { path: "/reports", permissions: ["CAN_VIEW_REPORTS", "CAN_EXPORT_REPORTS"] },
  { path: "/ota", permissions: ["CAN_MANAGE_OTA"] },
  { path: "/notifications", permissions: ["CAN_VIEW_LOGS"] },
];

/**
 * RoleRoute (Refactored to check permissions) - Guards routes based on the user's permissions.
 * Redirects unauthorized users to their first available page instead of causing redirect loops.
 */
const RoleRoute = ({ permissions = [], children }) => {
    const { user } = useSelector((state) => state.auth);
    const location = useLocation();

    const effectivePermissions = useMemo(() => {
        if (!user) return [];
        
        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        return rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') {
                return p.key || p.name || p.code || '';
            }
            return '';
        }).filter(Boolean);
    }, [user]);

    const isAllowed = useMemo(() => {
        if (!permissions || permissions.length === 0) return true;
        if (effectivePermissions.includes("*")) return true;
        return permissions.some(perm => effectivePermissions.includes(perm));
    }, [permissions, effectivePermissions]);

    const fallbackRoute = useMemo(() => {
        if (effectivePermissions.includes("*")) return "/dashboard";
        
        // Find the first route the user is allowed to access, excluding current path
        const firstAllowed = ALL_ROUTES.find(route => 
            route.path !== location.pathname &&
            (!route.permissions || 
            route.permissions.length === 0 || 
            route.permissions.some(perm => effectivePermissions.includes(perm)))
        );
        
        return firstAllowed ? firstAllowed.path : "/login";
    }, [effectivePermissions, location.pathname]);

    if (!isAllowed) {
        return <Navigate to={fallbackRoute} replace />;
    }

    return children;
};

export default RoleRoute;
